import { identity } from 'lodash-es';
import { AUTHENTICATION_SERVICE_BASEURL } from 'config';
import ServiceFactory, { returnVoid } from 'services/ServiceFactory';
import {
  FORGOT_PASSWORD_PATH,
  LOGIN_PATH,
  REGISTRATION_PATH,
  RESET_PASSWORD_PATH,
} from './constants';
import {
  AuthResponse,
  ForgotPasswordRequest,
  LoginRequest,
  ResetPasswordRequest,
  UserRegistrationRequest,
} from './types/api';

const serviceFactory = new ServiceFactory(AUTHENTICATION_SERVICE_BASEURL);

/**
 * Register a user
 */
export const registerUser = serviceFactory.createPostEndpoint(
  REGISTRATION_PATH,
)<UserRegistrationRequest, AuthResponse>({
  data: identity,
});

/**
 * User login
 *
 * Authentication to Headliner using email. If username doesn't exist or password
 * is incorrect, the response will have an http error code of 422 and have the
 * standard error response. Embedded in the error response will be a special
 * field called errorDetail. This is an object with additional error information.
 * If the user has historically used another platform to access their Headliner account,
 * a field called isNonSpareminAuthAvailable will be set to true.
 */
export const login = serviceFactory.createPostEndpoint(LOGIN_PATH)<
  LoginRequest,
  AuthResponse
>({
  data: identity,
});

/**
 * Forgot password
 *
 * Used when the user forgets the password. This endpoint will submit the request
 * to start the reset process. This action emails the user with a link to the
 * headliner web app's reset password page with the jwt token appended to the headliner
 * url. The jwt token should be passed through to the reset password endpoint in
 * order to complete the reset process.
 */
export const forgotPassword = serviceFactory.createPostEndpoint(
  FORGOT_PASSWORD_PATH,
)<ForgotPasswordRequest, void>({
  data: identity,
  select: returnVoid(),
});

/**
 * Reset user password
 *
 * Endpoint to reset the password. Requires the jwt token which should be provided
 * by the link in the email sent once the forgot password request is created.
 */
export const resetPassword = serviceFactory.createPostEndpoint(
  RESET_PASSWORD_PATH,
)<ResetPasswordRequest>({
  data: identity,
  select: returnVoid(),
});
